import { Alert, Form, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import { nameof } from 'src/core/utils/object';
import { container } from 'src/inversify.config';
import { CreateRoleAssignmentItem, RoleAssignmentItemStore, RoleAssignmentSummaryStore } from 'src/stores/role-assignments-store';

import GroupSelect from 'src/common/components/business/group-select';
import RolesSelect from 'src/common/components/business/roles-select';
import ScopeHierarchySelector from 'src/common/components/business/scope-hierarchy-selector';
import UserSelect from 'src/common/components/business/user-select';
import { IdentityType, ScopeType, Selector } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import NewItemModal from 'src/core/ui/newitemmodal';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';
import { Query } from '../../core/stores/data-store';
import {
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  NewLicensCreationAttributes,
  getUnlimitedLicenseAttributes,
} from '../../stores/licensing-store';
import { GlobalLicenseManagerRoleName, LicenseApproverRoleName, LicenseManagerRoleName } from '../../stores/roles-store';
import LicenseAttributesComponent from '../licenses/license-attributes/license-attributes';

interface NewRoleAssignmentFormData {
  identityType: IdentityType;
  identity: { value: string; label: string };
  roles: { value: string; label: string }[];
  hierarchy: { scopeId: string; scopeTitle: string; scopeType: ScopeType; parentId: string; path: string[] };
}

const NewRoleAssignmentDialog: FC<{ onClose: (response?: CommandResult<CreateRoleAssignmentItem>) => void }> = (props) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const { onClose } = props;
  const [form] = Form.useForm<NewRoleAssignmentFormData>();
  const [identityTypeValue] = useState<IdentityType>(IdentityType.User);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [allowedScopeRoot, setAllowedScopeRoot] = useState<string[]>([]);
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesStoreState = licenseCreationAttributesStore.state;
  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const newRoleAssignmentState = newRoleAssignmentStore.state;
  const roleAssignmentSummaryStore = container.get(RoleAssignmentSummaryStore);
  const roleAssignmentSummaryState = roleAssignmentSummaryStore.state;
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<boolean>(false);
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();
  const [customError, setCustomError] = useState('');
  const [licenseAttributesErrors, setLicenseAttributesErrors] = useState(false);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const [sptAbIsSelected, setIsSptAbSelected] = useState(false);
  const organizationSummaryStore = container.get(OrganizationSummaryStore);
  const organizationSummaryState = organizationSummaryStore.state;
  const allowedOrgs = useRef<string[]>([]);

  useEffect(() => {
    const loadPartners = async () => {
      await organizationSummaryStore.load({ filter: [`isPartner eq true`], take: 100000 });
      allowedOrgs.current = organizationSummaryState.items.value.map((v) => v.id);
      allowedOrgs.current.push(identity.profile['tenant_id']);
      setAllowedScopeRoot(allowedOrgs.current);
    };
    loadPartners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onNewItem = async () => {
    let item = (await form.validateFields()) as NewRoleAssignmentFormData;

    var query = {
      filter: {
        identityId: { value: item.identity.value, type: 'guid' },
        identityType: item.identityType,
        scopeId: { value: item.hierarchy.scopeId, type: 'guid' },
        roleExpirationDate: licenseAttributes?.roleExpirationDate,
      },
      skip: 0,
    } as Query;
    setCustomError('');
    var selectedRoles = item.roles.map((x) => {
      return { value: x.value, label: x.label };
    });
    roleAssignmentSummaryStore.loadLicenseAssignments(query).then(async () => {
      var existingRoles = verifyIfUserAlreadyHasSelectedRole(
        roleAssignmentSummaryState.items.value.filter((x) => x.scopeId === item.hierarchy.scopeId).map((x) => x.roleId),
        selectedRoles
      );
      if (existingRoles.length > 0) {
        setCustomError(t('SelectedUserAlreadyHasTheSelectedRoles').replace('{0}', existingRoles.join()));
      } else {
        const postData: CreateRoleAssignmentItem = {
          identityId: item.identity.value,
          identityType: item.identityType,
          roles: item.roles.map((r) =>
            r.label === LicenseManagerRoleName && licenseAttributes && licenseAttributes?.roleExpirationDate
              ? { role: r.label, roleId: r.value, expirationDate: licenseAttributes?.roleExpirationDate }
              : { roleId: r.value, role: r.label, expirationDate: undefined }
          ),
          scopeId: item.hierarchy.scopeId,
        };

        let response = await newRoleAssignmentStore.createLicenseRoleAssignment(postData);
        if (response?.isSuccess) {
          createLicenseAttributes(postData.identityId, postData.scopeId).then(() => {
            form.resetFields();
            handleClose(response);
          });
        }
      }
    });
  };
  const verifyIfUserAlreadyHasSelectedRole = (userRoles: string[], selectedRoles: { value: string; label: string }[]) => {
    var existingRoles = [];
    for (let i = 0; i < selectedRoles.length; i++) {
      if (userRoles.any((x) => x === selectedRoles[i].value)) {
        existingRoles.push(selectedRoles[i].label);
      }
    }
    return existingRoles;
  };
  const createLicenseAttributes = async (identityId: string, scopeId: string) => {
    if (licenseAttributes) {
      var item: NewLicensCreationAttributes = {
        identityType: identityTypeValue,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId,
        scopeId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }
      await licenseCreationAttributesStore.post(item);
    }
  };
  const onRolesChange = async (values: { value: string; label: string; selector: Selector }[]) => {
    var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
    if (!licenseAttributesVisible) {
      licenseMnagerRoleSelected && setLicenseAttributesVisible(true);
    } else {
      !licenseMnagerRoleSelected && setLicenseAttributesVisible(false);
      setLicenseAttributesErrors(false);
    }
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributes(item);
  };

  const getGroupFIlter = () => {
    const filters = allowedOrgs.current.map((v) => `organizationId eq ${v}`);
    return filters.join(' or ');
  };

  const handleClose = (response?: any) => {
    newRoleAssignmentStore.clearMessages();
    onClose(response);
  };

  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('NewRolesAssignment')}
      onNewItem={onNewItem}
      onClose={handleClose}
      onOkButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get() || licenseAttributesErrors}
      onCancelButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get()}
      closable={!newRoleAssignmentState.isBusy.get()}
      maskClosable={!newRoleAssignmentState.isBusy.get()}
      okText={t('Confirm')}
      errorMessage={newRoleAssignmentState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin
            spinning={
              newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get() || roleAssignmentSummaryState.isBusy.get()
            }
          >
            <Form size="middle" form={form} layout="vertical" autoComplete="off" initialValues={{ identityType: identityTypeValue, roles: [] }}>
              <Form.Item label={t('ChooseIdentity')} required>
                <Form.Item noStyle>
                  {identityTypeValue === 'User' && (
                    <Form.Item noStyle name={nameof<NewRoleAssignmentFormData>('identity')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                      <UserSelect
                        onChange={(_, organizations) => {
                          if (organizations) {
                            let filter = organizations?.filter((v) => allowedOrgs.current.includes(v)) || [];
                            if (filter.length === 0) filter = allowedOrgs.current;
                            setAllowedScopeRoot(filter);
                          } else {
                            setAllowedScopeRoot(allowedOrgs.current);
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                  {identityTypeValue === 'Group' && (
                    <Form.Item noStyle name={nameof<NewRoleAssignmentFormData>('identity')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                      <GroupSelect
                        onChange={(_, organization) => {
                          setAllowedScopeRoot(organization ? [organization] : allowedOrgs.current);
                        }}
                        filter={getGroupFIlter()}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Form.Item>
              <Form.Item
                name={nameof<NewRoleAssignmentFormData>('hierarchy')}
                rules={[
                  () => ({
                    required: true,
                    validator(_, value) {
                      if ((value?.scopeId || '').length === 0) return Promise.reject(t('PleaseSelect'));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <ScopeHierarchySelector
                  maxScope={ScopeType.Organization}
                  allowedScopeRoots={allowedScopeRoot}
                  onChange={async (x) => {
                    form.setFieldsValue({ ...form.getFieldsValue(), roles: [] });
                    const orgId = (x?.path || []).length < 2 ? undefined : x.path[1];
                    setSelectedOrg(orgId);
                    if (x.lastScope === ScopeType.Organization) {
                      if (x.scopeTitle && x.scopeTitle.toLowerCase() === 'sptab') {
                        setIsSptAbSelected(true);
                      } else {
                        setIsSptAbSelected(false);
                      }
                    } else {
                    }
                  }}
                />
              </Form.Item>

              <Form.Item name={nameof<NewRoleAssignmentFormData>('roles')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                <RolesSelect
                  maxSelector={Selector.Admin}
                  organizationId={selectedOrg}
                  onChange={onRolesChange}
                  excludeNames={[!sptAbIsSelected ? GlobalLicenseManagerRoleName : '']}
                  filterNames={[GlobalLicenseManagerRoleName, LicenseManagerRoleName, LicenseApproverRoleName]}
                />
              </Form.Item>
              {licenseAttributesVisible && (
                <LicenseAttributesComponent onChange={onLicenseAttributesChange} onFormValidation={(errors) => setLicenseAttributesErrors(errors)} />
              )}
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default NewRoleAssignmentDialog;
